import type { ManagerTeamQuery } from "@evolua/api-client";
import {
  Badge,
  EmptyState,
  FilterBar,
  PageHeader,
  SearchField,
  Select,
  Skeleton,
  Table,
} from "@evolua/ui";
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { QueryErrorState } from "../api/QueryErrorState.js";
import { useManagerTeam } from "../api/use-manager.js";
import { EmployeeDirectory } from "../components/employees/EmployeeDirectory.js";
import { hasPermission } from "../session/demo-session.js";
import { useSession } from "../session/SessionContext.js";

const PRESENCE_LABEL: Record<string, string> = {
  ACTIVE: "Ativo",
  ON_LEAVE: "Afastado",
  VACATION: "Férias",
  TERMINATED: "Desligado",
};

/** Visão de equipe do gestor (T43) ou diretório amplo (RH). */
export function EquipePage() {
  const { session } = useSession();
  const isManagerView =
    Boolean(session) &&
    hasPermission(session!, ["vacation:approve"]) &&
    !hasPermission(session!, ["lgpd:export_own"]);

  if (isManagerView) {
    return <ManagerTeamView />;
  }

  return (
    <EmployeeDirectory
      title="Equipe"
      lead="Listagem de colaboradores com filtros na URL, colunas configuráveis e exportação auditada"
      caption="Colaboradores da subárvore / diretório"
      detailBasePath="/equipe"
    />
  );
}

function ManagerTeamView() {
  const [params, setParams] = useSearchParams();
  const filters = useMemo((): ManagerTeamQuery => {
    const next: ManagerTeamQuery = { relation: "ALL" };
    const q = params.get("q");
    if (q) next.q = q;
    const relation = params.get("relation");
    if (relation === "DIRECT" || relation === "INDIRECT" || relation === "ALL") {
      next.relation = relation;
    }
    const status = params.get("status");
    if (status === "ACTIVE" || status === "ON_LEAVE" || status === "TERMINATED") {
      next.status = status;
    }
    return next;
  }, [params]);
  const [searchDraft, setSearchDraft] = useState(filters.q ?? "");
  const query = useManagerTeam(filters);

  if (query.isPending) {
    return (
      <div className="evolua-stack evolua-stack--2" aria-busy="true">
        <Skeleton height="2rem" />
        <Skeleton height="8rem" />
      </div>
    );
  }

  if (query.isError || !query.data) {
    return <QueryErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }

  const data = query.data;

  return (
    <>
      <PageHeader
        title="Minha equipe"
        lead="Subordinados diretos e indiretos. Salário oculto sem permissão específica."
      />

      <FilterBar>
        <SearchField
          label="Buscar"
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              const next = new URLSearchParams(params);
              if (searchDraft.trim()) next.set("q", searchDraft.trim());
              else next.delete("q");
              setParams(next, { replace: true });
            }
          }}
          placeholder="Nome ou área"
        />
        <Select
          label="Relação"
          value={filters.relation ?? "ALL"}
          options={[
            { value: "ALL", label: "Todos" },
            { value: "DIRECT", label: "Diretos" },
            { value: "INDIRECT", label: "Indiretos" },
          ]}
          onChange={(e) => {
            const next = new URLSearchParams(params);
            next.set("relation", e.target.value);
            setParams(next, { replace: true });
          }}
        />
        <Select
          label="Status"
          value={filters.status ?? ""}
          options={[
            { value: "", label: "Todos" },
            { value: "ACTIVE", label: "Ativo" },
            { value: "ON_LEAVE", label: "Afastado" },
            { value: "TERMINATED", label: "Desligado" },
          ]}
          onChange={(e) => {
            const next = new URLSearchParams(params);
            if (e.target.value) next.set("status", e.target.value);
            else next.delete("status");
            setParams(next, { replace: true });
          }}
        />
        <span className="evolua-muted">
          {data.total} · {data.directCount} diretos · {data.indirectCount} indiretos
        </span>
      </FilterBar>

      {data.data.length === 0 ? (
        <EmptyState
          title="Ninguém na equipe"
          description="Nenhum colaborador corresponde aos filtros na sua árvore."
        />
      ) : (
        <Table
          caption="Minha equipe"
          getRowId={(member) => member.id}
          columns={[
            {
              id: "name",
              header: "Nome",
              cell: (member) => (
                <Link to={`/equipe/${member.id}`}>{member.fullName}</Link>
              ),
            },
            { id: "dept", header: "Área", cell: (member) => member.departmentName },
            {
              id: "pos",
              header: "Cargo",
              priority: "optional",
              cell: (member) => member.positionName ?? "—",
            },
            {
              id: "rel",
              header: "Relação",
              priority: "optional",
              cell: (member) => (member.relation === "DIRECT" ? "Direto" : "Indireto"),
            },
            {
              id: "presence",
              header: "Presença",
              cell: (member) => (
                <Badge tone={member.presence === "ACTIVE" ? "success" : "warning"}>
                  {PRESENCE_LABEL[member.presence] ?? member.presence}
                </Badge>
              ),
            },
          ]}
          rows={data.data}
        />
      )}
    </>
  );
}
